/**
 * AlertDetailPanel - Detail view for selected alert in split layout
 *
 * Design: VERDICT-FIRST layout with the on-demand AI summary (RCA) leading, entity chips, known-vs-blind
 * panel, and compact event timeline. Always renders host link when hostId present.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import {
  Badge, Button, Card, Text, Heading, IconButton,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@agion/ui'
import {
  X, Server, AlertTriangle, Clock, Brain, Tag, ExternalLink,
  CheckCircle2, BellOff, Bell, Timer, Eye, EyeOff, ShieldCheck, CircleCheckBig,
} from 'lucide-react'
import { formatRelativeTime, cn } from '@/core/infra_utils'
import type { ActiveAlert, MonitoringHost, MuteDuration } from '@/core/infra_types'
import { deriveAlertName, deriveAlertMessage } from './alert_helpers'
import { SeverityBadge, SectionTitle } from './MonitoringPrimitives'
import { AlertRcaSection } from './AlertRcaSection'
import { AlertStatusButtons, AlertStatusSummary } from './AlertStatusControls'
import type { AlertStatusAction } from './infra_monitoring.service'

interface AlertDetailPanelProps {
  alert: ActiveAlert
  host: MonitoringHost | null
  onClose: () => void
  onSetStatus: (alertId: string, status: AlertStatusAction) => void
  onClearStatus: (alertId: string, status: AlertStatusAction) => void
  onMute: (alertId: string, duration: MuteDuration) => void
  onUnmute: (alertId: string) => void
  isMutating: boolean
}

export function AlertDetailPanel({
  alert,
  host,
  onClose,
  onSetStatus,
  onClearStatus,
  onMute,
  onUnmute,
  isMutating,
}: AlertDetailPanelProps) {
  const { t } = useTranslation(['pages'])

  const alertName = deriveAlertName(alert)
  const alertMessage = deriveAlertMessage(alert)
  const severityLabel = t(`pages:monitoring.alerts.severity.${alert.severity}`, alert.severity)

  const isMuted = alert.mutedUntil && new Date(alert.mutedUntil) > new Date()
  const isResolved = alert.status === 'resolved'
  const isHostOffline = host?.status === 'offline'

  const entityChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; value: string; link?: string }> = []

    if (host) {
      chips.push({
        key: 'host',
        label: t('pages:monitoring.alertDetail.host', 'Host'),
        value: host.name,
        link: `/hosts/${host.id}`,
      })
    } else if (alert.hostId) {
      chips.push({
        key: 'host',
        label: t('pages:monitoring.alertDetail.host', 'Host'),
        value: alert.hostId,
        link: `/hosts/${alert.hostId}`,
      })
    }

    if (alert.labels['service']) {
      chips.push({
        key: 'service',
        label: t('pages:monitoring.alertDetail.service', 'Service'),
        value: alert.labels['service'],
      })
    }

    chips.push({
      key: 'alertname',
      label: t('pages:monitoring.alertDetail.alertName', 'Alert'),
      value: alertName,
    })

    chips.push({
      key: 'severity',
      label: t('pages:monitoring.alertDetail.severityLabel', 'Severity'),
      value: severityLabel,
    })

    return chips
  }, [alert, host, alertName, severityLabel, t])

  const timeline = useMemo(() => {
    const events: Array<{ time: string; label: string; icon: typeof Clock }> = []

    events.push({
      time: alert.startsAt,
      label: t('pages:monitoring.alertDetail.timeline.started', 'Started'),
      icon: AlertTriangle,
    })

    if (alert.ackedAt) {
      events.push({
        time: alert.ackedAt,
        label: t('pages:monitoring.alertDetail.timeline.acked', 'On it: {{by}}', { by: alert.ackedBy }),
        icon: CheckCircle2,
      })
    }

    if (alert.mitigatedAt && alert.mitigatedBy) {
      events.push({
        time: alert.mitigatedAt,
        label: t('pages:monitoring.alertDetail.timeline.mitigated', 'Mitigated by {{by}}', { by: alert.mitigatedBy }),
        icon: ShieldCheck,
      })
    }

    if (alert.closedAt && alert.closedBy) {
      events.push({
        time: alert.closedAt,
        label: t('pages:monitoring.alertDetail.timeline.closed', 'Resolved by {{by}}', { by: alert.closedBy }),
        icon: CircleCheckBig,
      })
    }

    if (alert.mutedUntil && alert.mutedBy) {
      events.push({
        time: alert.mutedUntil,
        label: t('pages:monitoring.alertDetail.timeline.muted', 'Muted by {{by}} until {{until}}', {
          by: alert.mutedBy,
          until: formatRelativeTime(alert.mutedUntil),
        }),
        icon: BellOff,
      })
    }

    if (alert.resolvedAt) {
      events.push({
        time: alert.resolvedAt,
        label: t('pages:monitoring.alertDetail.timeline.resolved', 'Resolved'),
        icon: CheckCircle2,
      })
    }

    return events.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
  }, [alert, t])

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-900/50">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2 min-w-0">
          <SeverityBadge
            severity={alert.severity}
            label={severityLabel}
            ariaLabel={t('pages:monitoring.alerts.severityAriaLabel', '{{severity}} severity', { severity: severityLabel })}
            icon={AlertTriangle}
          />
          {isResolved && (
            <Badge variant="default">
              {t('pages:monitoring.alertDetail.status.resolved', 'Resolved')}
            </Badge>
          )}
        </div>
        <IconButton
          variant="ghost"
          size="sm"
          icon={<X size={16} />}
          aria-label={t('pages:monitoring.alertDetail.close', 'Close detail panel')}
          onClick={onClose}
        />
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <Heading level="h2" className="text-lg font-semibold break-words">
            {alertName}
          </Heading>
          {alertMessage && (
            <Text color="muted" className="mt-1 break-words">
              {alertMessage}
            </Text>
          )}
        </div>

        <AlertStatusSummary alert={alert} />

        {!isResolved && (
          <div className="flex flex-wrap items-center gap-2">
            <AlertStatusButtons
              alert={alert}
              isMutating={isMutating}
              onSetStatus={onSetStatus}
              onClearStatus={onClearStatus}
            />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={isMuted ? 'default' : 'secondary'}
                  size="sm"
                  disabled={isMutating}
                  className="flex items-center gap-2"
                >
                  {isMuted ? <BellOff size={14} className="text-amber-500" /> : <Bell size={14} />}
                  {isMuted ? t('pages:monitoring.alerts.muted', 'Muted') : t('pages:monitoring.alerts.mute', 'Mute')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {isMuted ? (
                  <DropdownMenuItem onClick={() => onUnmute(alert.fingerprint)}>
                    <Bell size={14} className="mr-2" />
                    {t('pages:monitoring.alerts.unmute', 'Unmute')}
                  </DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => onMute(alert.fingerprint, '1h')}>
                      <Timer size={14} className="mr-2" />
                      {t('pages:monitoring.alerts.mute1h', 'Mute for 1 hour')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onMute(alert.fingerprint, '4h')}>
                      <Timer size={14} className="mr-2" />
                      {t('pages:monitoring.alerts.mute4h', 'Mute for 4 hours')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onMute(alert.fingerprint, '24h')}>
                      <Timer size={14} className="mr-2" />
                      {t('pages:monitoring.alerts.mute24h', 'Mute for 24 hours')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {alert.hostId && (
              <Link to={`/hosts/${alert.hostId}`}>
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <Server size={14} />
                  {t('pages:monitoring.alertDetail.openHost', 'Open Host')}
                </Button>
              </Link>
            )}
          </div>
        )}

        <Card>
          <div className="p-4">
            <SectionTitle>
              <span className="inline-flex items-center gap-2">
                <Brain size={16} aria-hidden="true" />
                {t('pages:monitoring.alertDetail.rcaTitle', 'Root Cause Analysis')}
              </span>
            </SectionTitle>
            <AlertRcaSection fingerprint={alert.fingerprint} orgId={alert.orgId} />
          </div>
        </Card>

        <div className="flex flex-wrap gap-2">
          {entityChips.map(chip => (
            <EntityChip key={chip.key} label={chip.label} value={chip.value} link={chip.link} />
          ))}
        </div>

        <Card>
          <div className="p-4">
            <SectionTitle>
              <span className="inline-flex items-center gap-2">
                {isHostOffline ? <EyeOff size={16} /> : <Eye size={16} />}
                {t('pages:monitoring.alertDetail.telemetryTitle', 'Telemetry Status')}
              </span>
            </SectionTitle>
            <TelemetryStatusPanel host={host} />
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <SectionTitle>
              {t('pages:monitoring.alertDetail.timelineTitle', 'Event Timeline')}
            </SectionTitle>
            <div className="space-y-2">
              {timeline.map((event, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <event.icon size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <Text size="sm" className="break-words">{event.label}</Text>
                    <Text size="xs" color="muted">{formatRelativeTime(event.time)}</Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <SectionTitle>
              {t('pages:monitoring.alertDetail.contextTitle', 'Alert Context')}
            </SectionTitle>
            <KeyValueGrid
              entries={Object.entries(alert.labels).sort(([a], [b]) => a.localeCompare(b))}
              emptyLabel={t('pages:monitoring.alertDetail.noLabels', 'No labels')}
            />
          </div>
        </Card>
      </div>
    </div>
  )
}

function EntityChip({ label, value, link }: { label: string; value: string; link?: string }) {
  const content = (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm',
      'border border-slate-200 dark:border-slate-700',
      'bg-slate-50 dark:bg-slate-800',
      link && 'hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer'
    )}>
      <Tag size={12} className="text-slate-400" />
      <span className="text-slate-500 dark:text-slate-400">{label}:</span>
      <span className="font-medium text-slate-900 dark:text-slate-100 truncate max-w-[150px]">{value}</span>
      {link && <ExternalLink size={10} className="text-slate-400 ml-0.5" />}
    </span>
  )

  if (link) {
    return <Link to={link}>{content}</Link>
  }
  return content
}

function TelemetryStatusPanel({ host }: { host: MonitoringHost | null }) {
  const { t } = useTranslation(['pages'])

  if (!host) {
    return (
      <Text size="sm" color="muted">
        {t('pages:monitoring.alertDetail.noHostContext', 'No host context')}
      </Text>
    )
  }

  const isOffline = host.status === 'offline'
  const isDegraded = host.status === 'degraded'

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className={cn(
          'h-2 w-2 rounded-full',
          isOffline ? 'bg-rose-500' : isDegraded ? 'bg-amber-500' : 'bg-emerald-500'
        )} />
        <Text size="sm">
          {t(`pages:monitoring.hosts.status.${host.status}`, host.status)}
        </Text>
      </div>

      {isOffline && (
        <div className="rounded-md bg-rose-50 dark:bg-rose-950/30 p-3 border border-rose-200 dark:border-rose-900">
          <div className="flex items-start gap-2">
            <EyeOff size={14} className="text-rose-500 mt-0.5 flex-shrink-0" />
            <div>
              <Text size="sm" className="font-medium text-rose-700 dark:text-rose-300">
                {t('pages:monitoring.alertDetail.telemetryBlind', 'Telemetry blind')}
              </Text>
              <Text size="xs" color="muted" className="mt-0.5">
                {t('pages:monitoring.alertDetail.telemetryBlindDesc', 'Host is offline. Node-level metrics (CPU, memory, disk) are unavailable until the host reconnects.')}
              </Text>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex items-center gap-2">
          <Eye size={12} className={isOffline ? 'text-slate-300 dark:text-slate-600' : 'text-emerald-500'} />
          <Text size="xs" color={isOffline ? 'muted' : 'default'}>
            {t('pages:monitoring.alertDetail.nodeMetrics', 'Node metrics')}
          </Text>
        </div>
        <div className="flex items-center gap-2">
          <Eye size={12} className="text-emerald-500" />
          <Text size="xs">
            {t('pages:monitoring.alertDetail.alertmanager', 'Alertmanager')}
          </Text>
        </div>
      </div>
    </div>
  )
}

function KeyValueGrid({ entries, emptyLabel }: { entries: Array<[string, string]>; emptyLabel: string }) {
  if (entries.length === 0) {
    return <Text size="sm" color="muted">{emptyLabel}</Text>
  }

  return (
    <dl className="grid gap-2 sm:grid-cols-2">
      {entries.map(([key, value]) => (
        <div key={key} className="min-w-0 rounded-md border border-slate-200 px-2.5 py-1.5 dark:border-slate-800">
          <dt className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 truncate">
            {key}
          </dt>
          <dd className="break-words text-sm text-slate-900 dark:text-slate-100">{value}</dd>
        </div>
      ))}
    </dl>
  )
}
